import { CSSProperties } from 'react';
import { Nullable } from 'global';
import { v4 as uuidv4 } from 'uuid';
import { grey } from '@mui/material/colors';

import { CustomerVisibility } from '../../enums';
import { dealSheetInitialFormValues } from './constants';
import { DealSheetFormValues, LineItemType } from './types';
import { DealSheetConfigurationViewModel } from '../../types';

export function getInitialValues(config: Nullable<DealSheetConfigurationViewModel>): DealSheetFormValues {
  if (!config) return dealSheetInitialFormValues;

  return {
    name: config.name,
    description: config.description ?? '',

    taxes: config.charges.data
      .filter(charge => charge.type !== 'CHARGE')
      .map((charge, index) => ({
        ...charge,
        type: LineItemType.Tax,
        cap: charge.cap ?? null,
        id: charge.id ?? uuidv4(),
        parentId: charge.parentId ?? null,
        isEquipmentLvl: !!charge.equipmentId,
        sortOrder: charge.sortOrder ?? index,
        customerVisibility: charge.customerVisibility ?? CustomerVisibility.Visible,
      })),

    charges: config.charges.data
      .filter(charge => charge.type !== 'TAX')
      .map((charge, index) => ({
        ...charge,
        cap: charge.cap ?? null,
        type: LineItemType.Charge,
        id: charge.id ?? uuidv4(),
        parentId: charge.parentId ?? null,
        isEquipmentLvl: !!charge.equipmentId,
        sortOrder: charge.sortOrder ?? index,
        customerVisibility: charge.customerVisibility ?? CustomerVisibility.Visible,
      })),

    equipment: {
      ...dealSheetInitialFormValues.equipment,
      ...(config.dealRules.equipment ?? {}),
    },

    customer: {
      ...dealSheetInitialFormValues.customer,
      ...(config.dealRules.customer ?? {}),
    },

    address: {
      ...dealSheetInitialFormValues.address,
      ...(config.dealRules.location ?? {}),
      country: Array.isArray(config.dealRules.location?.country)
        ? config.dealRules.location.country[0]
        : dealSheetInitialFormValues.address.country,
    },
  };
}

const ROW_BACKGROUND_BY_VISIBILITY: Record<CustomerVisibility, string | undefined> = {
  [CustomerVisibility.Visible]: undefined,
  [CustomerVisibility.InternalOnly]: grey[100],
};

export const getLineItemRowStyles = ({ row }: { row: { customerVisibility?: CustomerVisibility } }): CSSProperties => {
  const background = row?.customerVisibility ? ROW_BACKGROUND_BY_VISIBILITY[row.customerVisibility] : undefined;
  return background ? { backgroundColor: background } : {};
};

export const getRemoveWithChildrenIndexes = (
  rowIndex: number,
  items: Array<{ id: string; parentId: string | null }>
): number[] => {
  const removedId = items[rowIndex]?.id;
  if (!removedId) return [rowIndex];

  const childrenByParentId = items.reduce<Map<string, string[]>>((acc, item) => {
    if (item.parentId) {
      const bucket = acc.get(item.parentId) ?? [];
      bucket.push(item.id);
      acc.set(item.parentId, bucket);
    }
    return acc;
  }, new Map());

  const descendantIds = new Set<string>([removedId]);
  const queue = [removedId];

  while (queue.length > 0) {
    const parentId = queue.shift() as string;
    const children = childrenByParentId.get(parentId) ?? [];

    children.forEach(childId => {
      if (!descendantIds.has(childId)) {
        descendantIds.add(childId);
        queue.push(childId);
      }
    });
  }

  return items.reduce<number[]>((acc, item, index) => {
    if (descendantIds.has(item.id)) acc.push(index);
    return acc;
  }, []);
};
